import React, { useMemo, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Tabs, Space } from 'antd';
import useForm, { FormContext } from '../../hooks/useForm';
import FormWrapper from '../FormWrapper';
import SaveButton from '../buttons/SaveButton';
import PageTitle from '../PageTitle';
import RoutesList from './RouteList';
import RoutePriceTable from '../routes/RoutePricesList';
import RoutesSelection from './RoutesSelection';
import CancelButton from '../buttons/CancelButton';
import usePermissions from '../../hooks/usePermission';

const permissionsMap = {
  canEdit: ['SYS_SUPERUSER', 'TAXONOMIES_PERMISSION'],
};

const EditRouteGroupForm = ({ routeGroup, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const { canEdit } = usePermissions(permissionsMap);
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t('errors.notEmpty')),
        routePrices: yup
          .array()
          .max(3, t('errors.maxThree')) // Custom error message for > 3 elements
          .required(t('errors.notEmpty')), // Optional: require the array itself
      }),
    [t],
  );

  const form = useForm({
    initialValue: routeGroup,
    schema: validationSchema,
    onSubmit,
    onCancel,
  });
  const { value, handleSubmit, input, setFormValue } = form;

  useEffect(() => {
    setFormValue(routeGroup);
  }, [setFormValue, routeGroup]);

  const { deleted } = value;

  return (
    <>
      <FormContext.Provider value={form}>
        <form onSubmit={handleSubmit}>
          <PageTitle
            title={routeGroup.name || t('entity.taxonomy.routeGroup._singular')}
          />

          <FormWrapper>
            <FormWrapper.Single>
              <FormWrapper.Input
                label={t('entity.taxonomy.routeGroup.name')}
                props={{ ...input('name'), disabled: deleted || !canEdit }}
              />

              <Space style={{ paddingTop: '1rem' }}>
                {!deleted && canEdit && <SaveButton />}
                <CancelButton onCancel={onCancel} />
              </Space>
            </FormWrapper.Single>

            <FormWrapper.Double>
              <Tabs>
                <Tabs.TabPane
                  tab={t('entity.taxonomy.routeGroup.tabs.selectedRoutes')}
                  key="sellers"
                >
                  <RoutesSelection />
                </Tabs.TabPane>

                <Tabs.TabPane
                  tab={t('entity.taxonomy.routeGroup.tabs.routes')}
                  key="pos"
                >
                  <RoutesList canEdit={canEdit} />
                </Tabs.TabPane>

                <Tabs.TabPane
                  tab={t('entity.taxonomy.route.price')}
                  key="route_price"
                >
                  <RoutePriceTable canEdit={canEdit} />
                </Tabs.TabPane>
              </Tabs>
            </FormWrapper.Double>
          </FormWrapper>
        </form>
      </FormContext.Provider>
    </>
  );
};
export default EditRouteGroupForm;
